import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { useConfig } from '@worklist-2/core/src/context/ConfigContext';
import { useAppModeContext } from '@worklist-2/core/src/context/AppModeContext';
import useGetSearchParams from '@rs-core/hooks/useGetSearchParams';
import { useStudiesStore } from '@worklist-2/worklist/src/DocumentViewerV3/store/studies';
import { useTranslation } from 'react-i18next';
import { useBooleanFlagValue, useNumberFlagValue } from '@rs-core/hooks/useFlags';
import pdfUtil from '@worklist-2/core/src/utils/pdfUtil';

import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import useGetBookmarkValue from '@worklist-2/worklist/src/DocumentViewerV3/hooks/useGetBookmarkValue';
import { useBlumeFormContext } from '@worklist-2/core/src/context/BlumeFormContext';

const PDFFiller = ({
	pdfSrc,
	pdfFileName,
	width = 880,
	height = '85vh',
	isReadOnly = false,
	showDownload = true,
	showPrint = true,
	groupButtonsProps,
	orderFormMapper,
}) => {
	// Hooks
	const { studyId, patientInfo, patientId, internalManagingOrganizationID } = useGetSearchParams();
	const { debounceSetPDFAnnotData, setPdfInstance, resetForm } = useBlumeFormContext();
	const { getBookmarkValue } = useGetBookmarkValue();
	const fetchStudies = useStudiesStore(state => state.getStudies);
	const currentStudy = useStudiesStore(state => state.currentStudy);

	// Feature Flags
	const mavenFormBookmark = useBooleanFlagValue('maven-2657-form-bookmark');
	const galaxyDvEnableDirectAccessLinkFix = useBooleanFlagValue('galaxy-dv-enable-direct-access-link-fix');
	const galaxyDvdiagnosticReportsCounts = useNumberFlagValue('galaxy-dv-diagnostic-reports-counts', 50);
	const mavenAddPageZoomBar = useBooleanFlagValue('maven-2702-add-page-zoom-bar');

	const { t } = useTranslation('form');

	// PDF container
	const containerRef = useRef(null);

	// The instace used to handle internal behavior
	const [instance, setInstance] = useState(null);

	// State to check the print dialog is open or not
	const [isPrintOpen, setIsPrintOpen] = useState(false);

	// The state to show/hide group buttons based on mouse over
	const [isMouseOver, setIsMouseOver] = useState(false);

	const [annotationLoaded, setAnnotationLoaded] = useState(false);

	// App config
	const __config = useConfig();

	// Patient portal information
	const { isPatientPortalMode } = useAppModeContext();
	const appModeIsPatientPortal = isPatientPortalMode();

	// Bookmark pattern
	const BOOKMARK_PATTERN = /@\{\{(.*?)\}\}/g;

	// Initial pdf instance
	useEffect(() => {
		const container = containerRef.current;
		let WebViewer;
		(async function () {
			// Fetch study data
			if (mavenFormBookmark && studyId) {
				await fetchStudies({
					__config,
					patientId: patientInfo || patientId,
					internalManagingOrganizationID,
					studyId,
					galaxyDvEnableDirectAccessLinkFix,
					galaxyDvdiagnosticReportsCounts,
					forceReload: true,
				});
			}

			if (instance) {
				if (pdfSrc) {
					instance.UI?.loadDocument(pdfSrc, { filename: pdfFileName });
				}
				return;
			}

			// Do not create instance when there are no any hosting.
			if (!__config.blume_base_url && !__config.omegaai_url) return;

			// Initial instance webviewer
			WebViewer = await import('@pdftron/webviewer');
			const localInstance = await WebViewer.default(
				{
					// Detect app is blume or worklist and load resources from public folder of them.
					path: appModeIsPatientPortal ? formatUrl(__config.blume_base_url) : formatUrl(__config.omegaai_url),
					licenseKey: __config.pdf_tron_license,
				},
				container
			);
			localInstance.UI.loadDocument(null, { onLoadingProgress: true });

			// Core instances
			const { FitMode, setTheme } = localInstance.UI;

			if (mavenAddPageZoomBar) {
				// Default theme is dark
				setTheme('dark');

				// Disable header
				localInstance.UI.disableElements([
					'viewControlsButton',
					'leftPanelButton',
					'ribbons',
					'toolsHeader',
					'toolStylePopup',
					'textPopup',
					'contextMenuPopup',
					'annotationCommentButton',
					'annotationStyleEditButton',
					'linkButton',
					'toggleNotesButton',
					'selectToolButton',
					'panToolButton',
					'settingsButton',
				]);

				const { style } = localInstance.UI.iframeWindow.document.documentElement;
				style.setProperty(`--view-header-background`, 'var(--gray-2)');
				style.setProperty(`--document-background-color`, 'var(--gray-3)');
			} else {
				// Disable header
				localInstance.UI.disableElements([
					'header',
					'pageNavOverlay',
					'toolsHeader',
					'toolStylePopup',
					'textPopup',
					'contextMenuPopup',
					'annotationCommentButton',
					'annotationStyleEditButton',
					'linkButton',
				]);
			}

			// Viewer fit with width
			localInstance.UI.setFitMode(FitMode.FitWidth);

			// Detect print dialog is open or not
			localInstance.UI.addEventListener('visibilityChanged', event => {
				if (event?.detail?.element === 'printModal') {
					setIsPrintOpen(event.detail.isVisible);
				}
			});

			setInstance(localInstance);
			if (setPdfInstance) {
				setPdfInstance(localInstance);
			}
		})();
		return () => {
			// Remove from cache
			window.URL.revokeObjectURL(pdfSrc);
			if (instance) {
				instance.UI.closeDocument();
			}
			resetForm();
		};
	}, []);

	useEffect(() => {
		if (mavenFormBookmark && instance && annotationLoaded) {
			const { annotationManager, Annotations } = instance.Core;

			setTimeout(() => {
				const annots = annotationManager.getAnnotationsList();
				annots.forEach(annot => {
					if (
						annot instanceof Annotations.TextWidgetAnnotation &&
						annot.sg?.tagName === 'INPUT' &&
						!isReadOnly
					) {
						const inputElement = annot?.zf?.children?.[0];
						if (inputElement) {
							inputElement.addEventListener('drop', e => {
								const data = e.dataTransfer.getData('bookmark');
								const divContainer = document.createElement('div');
								divContainer.innerHTML = data;

								if (divContainer.querySelector('span')?.getAttribute('data-code')) {
									const input = e.target;
									const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
										window.HTMLInputElement.prototype,
										'value'
									).set;
									nativeInputValueSetter.call(
										input,
										`${e.target.value}@{{${divContainer
											.querySelector('span')
											.getAttribute('data-code')
											.toString()}}}`
									);
									const event = new Event('input', { bubbles: true });
									input.dispatchEvent(event);
								}
								e.preventDefault();
							});

							inputElement.addEventListener('input', e => {
								const input = e.target;
								const newValue = input.value;

								if (annot && annot.getContents() !== newValue) {
									annot.getField()?.setValue(newValue);
									debounceSetPDFAnnotData();
								}
							});
							annot?.zf.addEventListener('dragenter', event => {
								event.preventDefault();
							});

							annot?.zf.addEventListener('dragover', event => {
								event.preventDefault();
							});
						}
					}
				});
			}, 2000);
		}
	}, [annotationLoaded, instance, isReadOnly, mavenFormBookmark]);

	// Load document
	useEffect(() => {
		async function asynchronousEffect() {
			if (instance && pdfSrc) {
				await instance.UI.loadDocument(pdfSrc, { filename: pdfFileName });
				const { annotationManager, documentViewer, Annotations } = instance.Core;
				const signatureTool = documentViewer.getTool(instance.Core.Tools.ToolNames.SIGNATURE);

				documentViewer.addEventListener('documentLoaded', async () => {
					// Core instances
					const { FitMode } = instance.UI;
					instance.UI.setFitMode(FitMode.FitWidth);
				});

				// Disable rotation control in case new signature
				signatureTool.addEventListener('signatureReady', signature => {
					signature.disableRotationControl();
				});

				documentViewer.addEventListener('annotationsLoaded', () => {
					const annots = annotationManager.getAnnotationsList();
					annots.forEach(annot => {
						if (annot instanceof Annotations.WidgetAnnotation) {
							annot.fieldFlags.set('ReadOnly', isReadOnly);
						}

						if (annot instanceof Annotations.SignatureWidgetAnnotation && annot.annot) {
							annot.annot.NoMove = isReadOnly;
							// Disable rotation control in case existed signature
							annot.annot.disableRotationControl();
						}
					});

					setAnnotationLoaded(true);

					// Fill bookmark
					if (mavenFormBookmark) {
						fillBookMark(annotationManager);
					}
				});

				if (!isReadOnly) {
					// Fill color for input fields
					Annotations.WidgetAnnotation.getCustomStyles = widget => {
						if (widget instanceof Annotations.TextWidgetAnnotation && widget.sg?.tagName === 'INPUT') {
							// Set color for readOnly field when editing the file
							if (widget.fieldFlags.get('ReadOnly') === true) {
								return {
									'background-color': 'lightgray',
									color: 'black',
								};
							}
							return {
								'background-color': 'lightblue',
								color: 'black',
							};
						}
						if (widget instanceof Annotations.PushButtonWidgetAnnotation) {
							return {
								'background-color': 'black',
								color: 'white',
							};
						}
						if (
							widget instanceof Annotations.CheckButtonWidgetAnnotation ||
							widget instanceof Annotations.RadioButtonWidgetAnnotation
						) {
							return {
								'background-color': 'lightgray',
								opacity: 0.8,
							};
						}
					};
				} else {
					Annotations.WidgetAnnotation.getCustomStyles = () => ({});
					// Disable signature tool and turn on read only mode
					annotationManager.enableReadOnlyMode();
					instance.UI.disableElements(['annotationPopup']);
				}
			}
		}

		asynchronousEffect();
	}, [pdfSrc, instance, isReadOnly]);

	/**
	 * fillBookMark
	 * @param annotationManager
	 */
	const fillBookMark = annotationManager => {
		if ((studyId && currentStudy) || orderFormMapper) {
			const fieldManager = annotationManager.getFieldManager();
			const fields = fieldManager.getFields();

			fields.forEach(field => {
				const matchBookmarks = field.value.match(BOOKMARK_PATTERN);

				if (matchBookmarks) {
					const bookMarkValueString = field.value.replace(BOOKMARK_PATTERN, (_, code) => {
						const bookMarkValue = orderFormMapper ? orderFormMapper[code] : getBookmarkValue({ code });
						return bookMarkValue && bookMarkValue !== 'N/A' ? bookMarkValue : '';
					});

					// Set value for bookmark
					field.setValue(bookMarkValueString);

					// Do not disable input incase bookmark value is empty
					if (bookMarkValueString.trim() !== '') {
						field.flags.set('ReadOnly', true);
						field.widgets?.forEach(widget => {
							const { sg } = widget;
							if (sg) {
								sg.style.backgroundColor = 'lightgray';
								sg.style.color = 'black';
							}
						});
					}
				}
			});
		}
	};

	/**
	 * formatUrl
	 *  Detect and fix wrong url format
	 * @param {*} url
	 * @returns
	 */
	const formatUrl = url => {
		if (!url) return;
		return `${url.endsWith('/') ? url : `${url}/`}webviewer`;
	};

	/**
	 * handleClickDownload
	 * down load pdf content
	 */
	const handleClickDownload = () => {
		pdfUtil.download(instance, pdfFileName);
	};

	/**
	 * handleClickPrint
	 *  Print pdf
	 */
	const handleClickPrint = () => {
		pdfUtil.print(instance);
	};

	return (
		<Box
			data-testid="pdf-container"
			sx={{
				height,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				width,
				backgroundColor: 'transparent',
			}}
			onMouseLeave={() => {
				setIsMouseOver(false);
			}}
			onMouseOver={() => {
				setIsMouseOver(true);
			}}
		>
			{isReadOnly && (
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						width,
						height: '35px',
						backgroundColor: 'rgb(232,232,232)',
						borderRadius: '10px 10px 0 0',
						textAlign: 'center',
						verticalAlign: 'middle',
						color: 'rgb(121,121,121)',
					}}
				>
					<LockOutlinedIcon />
					<Box
						sx={{
							color: 'rgb(121,121,121)',
						}}
					>
						{t('Readonly')}
					</Box>
				</Box>
			)}
			<div ref={containerRef} style={{ width: '100%', height }} />
			{!mavenAddPageZoomBar && isMouseOver && !isPrintOpen && (
				<Box
					sx={{
						bottom: '10px',
						position: 'absolute',
						display: 'flex',
						gap: '6px',
						zIndex: 10,
						left: '50%',
						transform: 'translateX(-50%)',
						...groupButtonsProps?.sx,
					}}
				>
					{showDownload && (
						<Tooltip title={t('Download')}>
							<Box
								sx={{
									padding: '11px',
									background: '#393939',
									borderRadius: '90px',
									boxShadow: '-2px 4px 5px rgba(0, 0, 0, 0.05)',
									width: '40px',
									height: '40px',
									boxSizing: 'border-box',
									position: 'relative',
									cursor: 'pointer',
								}}
								onClick={handleClickDownload}
							>
								<DownloadOutlinedIcon
									sx={{
										color: 'rgba(255, 255, 255, 0.6)',
										position: 'absolute',
										top: '50%',
										left: '50%',
										transform: 'translate(-50%,-50%)',
									}}
								/>
							</Box>
						</Tooltip>
					)}
					{showPrint && (
						<Tooltip title={t('Print')}>
							<Box
								sx={{
									padding: '11px',
									background: '#393939',
									borderRadius: '90px',
									boxShadow: '-2px 4px 5px rgba(0, 0, 0, 0.05)',
									width: '40px',
									height: '40px',
									boxSizing: 'border-box',
									position: 'relative',
									cursor: 'pointer',
								}}
								onClick={handleClickPrint}
							>
								<PrintOutlinedIcon
									sx={{
										color: 'rgba(255, 255, 255, 0.6)',
										position: 'absolute',
										top: '50%',
										left: '50%',
										transform: 'translate(-50%,-50%)',
									}}
								/>
							</Box>
						</Tooltip>
					)}
				</Box>
			)}
		</Box>
	);
};

export default PDFFiller;
